import React, { useEffect, useState } from "react";
import DashboardLayout from "../../DashboardLayout";
import apiClient from "../../../utils/apiClient";
import FyersUsersListTable from "./fyersUserTable";
import { AddNewUserModal } from "./addNewUserModal";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { ChevronsUpDown, Search, X } from "lucide-react";
import toast from "react-hot-toast";
import { useMemo } from "react";
import server from "../../../utils/serverConfig";

export default function FyersUserList() {
  const [fyersUsersData, setFyersUsersData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [statusFilters, setStatusFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBalanceUpdating, setIsBalanceUpdating] = useState(false);

  //  Add this state for search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Search and filter function
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  // Enhanced filtered data logic (combine with your existing status filter)
  const getFilteredData = useMemo(() => {
    let filtered = fyersUsersData;

    // Apply status filter first (your existing logic)
    if (Object.values(statusFilters).some(Boolean)) {
      filtered = filtered.filter(user => statusFilters[user.status]);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(user =>
        user.client_id.toLowerCase().includes(search) ||
        user.name.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [fyersUsersData, statusFilters, searchTerm]);

  const fetchFyersUserList = () => {
    let config = {
      method: "get",
      // url: `https://godseye.server.sypto.xyz/get-fyers-users`,
      url: `${server.godseye.baseUrl}get-fyers-users`,
    };

    apiClient
      .request(config)
      .then((response) => {
        setFyersUsersData(response.data);
        setFilteredData(response.data);

        // Extract unique statuses and initialize filters
        const uniqueStatuses = [
          ...new Set(response.data.map((user) => user.status)),
        ];
        setStatusFilters(
          uniqueStatuses.reduce((acc, status) => {
            acc[status] = true; // Initialize all statuses as checked
            return acc;
          }, {})
        );
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    fetchFyersUserList();
  }, []);

  console.log("Filtered Data:", filteredData);

  const handleFilterChange = (status) => {
    const updatedFilters = {
      ...statusFilters,
      [status]: !statusFilters[status],
    };
    setStatusFilters(updatedFilters);

    const activeFilters = Object.entries(updatedFilters)
      .filter(([_, isChecked]) => isChecked)
      .map(([status]) => status);

    const filtered = activeFilters.length
      ? fyersUsersData.filter((user) => activeFilters.includes(user.status))
      : fyersUsersData; // Show all data if no filters are active
    setFilteredData(filtered);
  };

  const resetFilters = () => {
    const resetFiltersState = Object.keys(statusFilters).reduce(
      (acc, status) => {
        acc[status] = true;
        return acc;
      },
      {}
    );
    setStatusFilters(resetFiltersState);
    setFilteredData(fyersUsersData); // Reset to show all data
    setSearchTerm(''); // Clear search term
  };

  const handleGenerateAccessToken = () => {
    setIsLoading(true);

    console.log("Generating Access Token...");

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}generate_fyers_access_token`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({}),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Access Token generation response:", response.data);

        if (response.data.status === 200) {
          toast.success("Access Token generated Successfully !!", {
            duration: 5000,
          });
          console.log("Access Token generated Successfully !!");

          setIsLoading(false);
        } else {
          console.log("Access Token generation failed:", response.data);
          toast.error("Access Token generation Failed !!", {
            duration: 5000,
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.log("Error in position request:", error);
      });
  };

  const handleAddFyersUserIndexMuliplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    setIsLoading(true);

    console.log(" Adding multiplier...");

    const payload = {
      client_id: client_id.trim(),
      name: name.trim(),
      broker: "fyers",
      indexMultiplier: parseInt(multiplier),
      bankniftyMultiplier: parseInt(bankniftyMultiplier),
      finniftyMultiplier: parseInt(finniftyMultiplier),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier),
      sensexMultiplier: parseInt(sensexMultiplier),
    }

    console.log("Payload for adding multiplier:", payload);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-index-options-multiplier`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Muliplier Added Successfully :", response.data);

        if (response.data.status === 200) {
          toast.success("Index Options Muliplier Added Successfully !!", {
            duration: 5000,
          });
          console.log("Muliplier Added Successfully !!");

          setIsLoading(false);
          fetchFyersUserList()
        } else {
          console.log("Failed to add Muliplier !!", response.data);
          toast.error("Failed to add Muliplier !!", {
            duration: 5000,
          });
          setIsLoading(false);
          fetchFyersUserList()
        }
      })
      .catch((error) => {
        console.log("Error in adding client multiplier data:", error);
        fetchFyersUserList()
      });
  };

  const handleUpdateUserBalance = () => {
    setIsBalanceUpdating(true);

    console.log("Calling Update balalnce ...");

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}update-fyers-user-balance`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({}),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("update fyers user balance response:", response.data);

        if (response.data.status === 200) {
          toast.success("Fyers User balance UpdatedSuccessfully !!", {
            duration: 5000,
          });
          console.log("Fyers User balance Updated Successfully !!");

          setIsBalanceUpdating(false);
        } else {
          console.log("Balalnce Update failed:", response.data);
          toast.error("Balalnce Update Failed !!", {
            duration: 5000,
          });
          setIsBalanceUpdating(false);
        }
      })
      .catch((error) => {
        console.log("Error in balance update request:", error);
        setIsBalanceUpdating(false);
      });
  };

  const handleAddNubraUserIndexMuliplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    setIsLoading(true);

    console.log(" Adding multiplier...");

    const payload = {
      client_id: client_id.trim(),
      name: name.trim(),
      broker: "nubra",
      indexMultiplier: parseInt(multiplier),
      bankniftyMultiplier: parseInt(bankniftyMultiplier),
      finniftyMultiplier: parseInt(finniftyMultiplier),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier),
      sensexMultiplier: parseInt(sensexMultiplier),
    }

    console.log("Payload for adding multiplier:", payload);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-index-options-multiplier`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Muliplier Added Successfully :", response.data);

        if (response.data.status === 200) {
          toast.success("Index Options Muliplier Added Successfully !!", {
            duration: 5000,
          });
          console.log("Muliplier Added Successfully !!");

          setIsLoading(false);
          fetchFyersUserList()
        } else {
          console.log("Failed to add Muliplier !!", response.data);
          toast.error("Failed to add Muliplier !!", {
            duration: 5000,
          });
          setIsLoading(false);
          fetchFyersUserList()
        }
      })
      .catch((error) => {
        console.log("Error in adding client multiplier data:", error);
        fetchFyersUserList()
      });
  };

  const handleAddDhanXTSUserIndexMultiplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    setIsLoading(true);

    console.log(" Adding multiplier...");

    const payload = {
      client_id: client_id.trim(),
      name: name.trim(),
      broker: "dhan_xts",
      indexMultiplier: parseInt(multiplier),
      bankniftyMultiplier: parseInt(bankniftyMultiplier),
      finniftyMultiplier: parseInt(finniftyMultiplier),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier),
      sensexMultiplier: parseInt(sensexMultiplier),
    }

    console.log("Payload for adding multiplier:", payload);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-index-options-multiplier`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Muliplier Added Successfully :", response.data);

        if (response.data.status === 200) {
          toast.success("Index Options Muliplier Added Successfully !!", {
            duration: 5000,
          });
          console.log("Muliplier Added Successfully !!");

          setIsLoading(false);
          fetchFyersUserList()
        } else {
          console.log("Failed to add Muliplier !!", response.data);
          toast.error("Failed to add Muliplier !!", {
            duration: 5000,
          });
          setIsLoading(false);
          fetchFyersUserList()
        }
      })
      .catch((error) => {
        console.log("Error in adding client multiplier data:", error);
        fetchFyersUserList()
      });
  };

  const handleAddDhanHQUserIndexMultiplier = (client_id, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier) => {
    setIsLoading(true);

    console.log(" Adding multiplier...");

    const payload = {
      client_id: client_id.trim(),
      name: name.trim(),
      broker: "dhan_hq",
      indexMultiplier: parseInt(multiplier),
      bankniftyMultiplier: parseInt(bankniftyMultiplier),
      finniftyMultiplier: parseInt(finniftyMultiplier),
      midcpniftyMultiplier: parseInt(midcpniftyMultiplier),
      sensexMultiplier: parseInt(sensexMultiplier),
    }

    console.log("Payload for adding multiplier:", payload);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-index-options-multiplier`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload),
    };

    apiClient
      .request(config)
      .then((response) => {
        console.log("Muliplier Added Successfully :", response.data);

        if (response.data.status === 200) {
          toast.success("Index Options Muliplier Added Successfully !!", {
            duration: 5000,
          });
          console.log("Muliplier Added Successfully !!");

          setIsLoading(false);
          fetchFyersUserList()
        } else {
          console.log("Failed to add Muliplier !!", response.data);
          toast.error("Failed to add Muliplier !!", {
            duration: 5000,
          });
          setIsLoading(false);
          fetchFyersUserList()
        }
      })
      .catch((error) => {
        console.log("Error in adding client multiplier data:", error);
        fetchFyersUserList()
      });
  };


  //  console.log("Fyers Users Data:", fyersUsersData);
  return (
    <div className="font-inter flex flex-col relative w-full max-h-screen">
      <div className="flex items-center justify-between space-x-4">
        <div className="flex justify-between items-center h-[40px] px-6 w-full">
          <p className="flex items-center text-lg font-semibold text-white">

            {/* Search Input */}


            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by Client ID or Name..."
                value={searchTerm}

                onChange={(e) => handleSearch(e.target.value)}
                className="w-full font-medium text-sm pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>



          </p>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              disabled={isLoading}
              onClick={handleGenerateAccessToken}
              className={`text-white font-medium`}
            >
              {isLoading ? "Generating..." : "Generate Access Token"}
            </Button>

            <Button
              variant="outline"
              disabled={isBalanceUpdating}
              onClick={handleUpdateUserBalance}
              className={`text-white font-medium`}
            >
              {isLoading ? "Updating..." : "Update Available Balance"}
            </Button>
            <div className="flex items-center">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-[250px] justify-between text-white text-[15px]"
                  >
                    Filter by Status
                    <ChevronsUpDown className="opacity-80 h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0 border-zinc-800">
                  <div className="flex flex-col space-y-2 p-2">
                    {Object.keys(statusFilters).map((status) => (
                      <div key={status} className="flex items-center space-x-2">
                        <Checkbox
                          id={status}
                          checked={statusFilters[status]}
                          onCheckedChange={() => handleFilterChange(status)}
                        />
                        <label
                          htmlFor={status}
                          className="text-sm text-white cursor-pointer"
                        >
                          {status}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <Button
              variant="outline"
              onClick={resetFilters}
              className="text-white font-medium"
            >
              Reset Filters
            </Button>
            <AddNewUserModal
              handleAddFyersUserIndexMuliplier={handleAddFyersUserIndexMuliplier}
              handleAddNubraUserIndexMuliplier={handleAddNubraUserIndexMuliplier}
              handleAddDhanXTSUserIndexMultiplier={handleAddDhanXTSUserIndexMultiplier}
              handleAddDhanHQUserIndexMultiplier={handleAddDhanHQUserIndexMultiplier}
            />
          </div>
        </div>
      </div>

      <div>
        <FyersUsersListTable
          fyersUsersData={getFilteredData}
          fetchFyersUserList={fetchFyersUserList}
        />
      </div>
    </div>
  );
}
